import { FC, useEffect, useState } from 'react';
import { useHomeQuery } from '../graphql/generated/operations/home.ts';
import { LoadingBackdrop } from '../components/loading.backdrop.tsx';
import { Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../constants/paths.ts';
import { DayDisplay } from '../components/activity/day-display.tsx';
import { format, getDay, startOfWeek, subHours } from 'date-fns';
import { useMobile } from '../contexts/mobile.context.tsx';
import { useGetNotificationsQuery } from '../graphql/generated/operations/messages.ts';
import { trackEvent } from '../utils/analytics.ts';

export const HomePage: FC = () => {
  const { data, loading } = useHomeQuery({
    fetchPolicy: 'network-only',
    variables: {
      offset: Math.floor(new Date().getTimezoneOffset() / 60) // Timezone offset in hours
    }
  });
  const navigate = useNavigate();
  const [upcomingDays, setUpcomingDays] = useState<number[]>([]);
  const [previousDays, setPreviousDays] = useState<number[]>([]);
  const { sendMessage } = useMobile();
  const { data: allNotifications } = useGetNotificationsQuery({
    variables: {
      offset: Math.floor(new Date().getTimezoneOffset() / 60) // Timezone offset in hours
    }
  });

  useEffect(() => {
    sendMessage('READY', '');
    sendMessage('NOTIFICATIONS', 'CheckPermissions');
  }, []);

  useEffect(() => {
    if (data?.needToCompleteRegistration) {
      navigate(Paths.WELCOME);
    }
  }, [data]);

  useEffect(() => {
    if (allNotifications?.notifications) {
      sendMessage('NOTIFICATIONS', 'NewNotifications', allNotifications.notifications);
    }
  }, [allNotifications]);

  useEffect(() => {
    const today = getDay(new Date());
    const upcomingDays = [];
    const previousDays = [];
    for (let i = today - 1; i < 7; i++) {
      upcomingDays.push(i);
    }
    for (let i = 0; i < today - 1; i++) {
      previousDays.push(i);
    }
    setUpcomingDays(upcomingDays);
    setPreviousDays(previousDays);
  }, []);

  if (loading) {
    return <LoadingBackdrop />;
  }
  return (
    <Stack spacing={2}>
      {data?.isFirstWeek && !data?.needToScheduleActivity && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h5">Congrats on planning your first week of activities!</Typography>
          </CardContent>
        </Card>
      )}
      {(data?.needToScheduleActivity || data?.needToAnswerDailyLog || data?.needToAnswerWeeklyLog) && <Typography variant="h4">Action Items</Typography>}
      {data?.needToScheduleActivity && (
        <Card
          variant="outlined"
          onClick={() => {
            const hasPreviousWeekActivities = (data?.previousActivities?.length ?? 0) > 0;
            const destination = hasPreviousWeekActivities ? Paths.REPLAN_ACTIVITES : Paths.ACTIVITY_PLANNING;
            navigate(destination);
            trackEvent('action_item_click', { destination });
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center">
              <Typography variant="h5" flexGrow={1}>
                {(() => {
                  const offset = Math.floor(new Date().getTimezoneOffset() / 60);
                  const currentWeek = format(startOfWeek(subHours(new Date(), offset), { weekStartsOn: 1 }), 'yyyy-MM-dd');
                  const currentWeekActivities = data?.activities?.filter((a) => a.weekOf === currentWeek) ?? [];
                  const count = currentWeekActivities.length;
                  const ordinal = count === 0 ? 'first' : count === 1 ? 'second' : 'next';
                  return `Schedule your ${ordinal} activity for this week`;
                })()}
              </Typography>
              <FontAwesomeIcon icon={faChevronRight} />
            </Stack>
          </CardContent>
        </Card>
      )}
      {data?.needToAnswerDailyLog && (
        <Card
          variant="outlined"
          onClick={() => {
            navigate(Paths.DAILY_LOG);
            trackEvent('action_item_click', { destination: Paths.DAILY_LOG });
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center">
              <Typography variant="h5" flexGrow={1}>
                Complete your Daily Log
              </Typography>
              <FontAwesomeIcon icon={faChevronRight} />
            </Stack>
          </CardContent>
        </Card>
      )}
      {data?.needToAnswerWeeklyLog && (
        <Card
          variant="outlined"
          onClick={() => {
            navigate(Paths.WEEKLY_LOG);
            trackEvent('action_item_click', { destination: Paths.WEEKLY_LOG });
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center">
              <Typography variant="h5" flexGrow={1}>
                Complete your Weekly Log
              </Typography>
              <FontAwesomeIcon icon={faChevronRight} />
            </Stack>
          </CardContent>
        </Card>
      )}
      {(data?.needToScheduleActivity || data?.needToAnswerDailyLog || data?.needToAnswerWeeklyLog) && <Divider />}
      {!data?.activities && (
        <>
          <Typography variant="h4">Upcoming Activities</Typography>
          <Divider />
        </>
      )}
      {upcomingDays.map((day) => (
        <DayDisplay key={day} day={day} activities={data?.activities} />
      ))}
      {previousDays && previousDays.length > 0 && (
        <>
          <Typography variant="h4">Previous Activities</Typography>
          <Divider />
        </>
      )}
      {previousDays.map((day) => (
        <DayDisplay key={day} day={day} activities={data?.activities} />
      ))}
      <Box sx={{ height: '3rem' }} />
    </Stack>
  );
};
